import { InstallHint } from "@/ui/InstallHint.tsx";
import type { PushPrefs, PushSubscriptionInfo } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellOff, BellRing, Send, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { pushApi } from "@/api/push.ts";
import { currentSubscription, devHttpsUrl, disablePush, enablePush, isIos, isStandalone, pushSupport } from "@/lib/push.ts";
import { Alert, Button, Card, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

const KINDS = ["runs", "approvals", "schedules"] as const;

export function NotificationsSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toMsg = useApiError();
  const support = pushSupport();
  const list = useQuery({ queryKey: ["push", "subs"], queryFn: pushApi.list });
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [perm, setPerm] = useState<NotificationPermission | "n/a">(typeof Notification === "undefined" ? "n/a" : Notification.permission);
  useEffect(() => {
    if (support === "ok") void currentSubscription().then((s) => setEndpoint(s?.endpoint ?? null));
  }, [support]);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["push", "subs"] });
  const enable = useMutation({
    mutationFn: enablePush,
    onSuccess: (r) => { setPerm(Notification.permission); if (r === "granted") { refresh(); void currentSubscription().then((s) => setEndpoint(s?.endpoint ?? null)); } },
  });
  const disable = useMutation({ mutationFn: disablePush, onSuccess: () => { setEndpoint(null); refresh(); } });
  const update = useMutation({ mutationFn: ({ id, prefs }: { id: string; prefs: Partial<PushPrefs> }) => pushApi.update(id, { prefs }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => pushApi.remove(id), onSuccess: refresh });
  const { confirm } = useConfirm();
  const askRemove = async (id: string, label: string) => { if (await confirm({ title: t("confirm.removeDeviceTitle"), message: t("confirm.removeDeviceMsg", { name: label }), confirmLabel: t("common.remove"), danger: true })) remove.mutate(id); };
  const test = useMutation({ mutationFn: (id?: string) => pushApi.test(id) });
  const thisDevice = list.data?.find((s) => s.endpoint === endpoint);

  return (
    <div className="space-y-4">
      <InstallHint always />
      <Card className="space-y-3">
        <h3 className="font-semibold">{t("notifications.title")}</h3>
        <p className="text-sm text-fg-muted">{t("notifications.intro")}</p>
        {support === "unsupported" && <Alert tone="warn">{t("notifications.unsupported")}</Alert>}
        {support === "insecure" && (
          <Alert tone="warn">
            <div>{t("notifications.insecure")}</div>
            {devHttpsUrl() && <a className="mt-1 block break-all font-medium underline" dir="ltr" href={devHttpsUrl()!}>{devHttpsUrl()}</a>}
            {devHttpsUrl() && isIos() && <div className="mt-1 text-xs">{t("notifications.iosTrust")}</div>}
          </Alert>
        )}
        {support === "install" && <Alert tone="warn">{t("notifications.iosInstall")}</Alert>}
        {support === "ok" && perm === "denied" && <Alert tone="danger">{t("notifications.denied")}</Alert>}
        {enable.data === "failed" && <Alert tone="danger">{t("notifications.failed")}</Alert>}
        <p className="text-[11px] text-fg-muted" dir="ltr">{`HTTPS ${window.isSecureContext ? "✓" : "✗"} · ${isIos() ? `Home Screen app ${isStandalone() ? "✓" : "✗"} · ` : ""}permission: ${perm}`}</p>
        {(enable.error || disable.error || test.error) && <Alert tone="danger">{toMsg(enable.error ?? disable.error ?? test.error)}</Alert>}
        <div className="flex flex-wrap items-center gap-2">
          {thisDevice ? (
            <>
              <span className="flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-accent"><BellRing className="size-3.5" /> {t("notifications.enabledHere")}</span>
              <Button size="sm" variant="secondary" onClick={() => test.mutate(thisDevice.id)} loading={test.isPending}><Send className="size-3.5" /> {t("notifications.sendTest")}</Button>
              <Button size="sm" variant="ghost" onClick={() => disable.mutate()} loading={disable.isPending}><BellOff className="size-3.5" /> {t("notifications.disableHere")}</Button>
            </>
          ) : (
            <Button onClick={() => enable.mutate()} loading={enable.isPending} disabled={support !== "ok" || perm === "denied"}><BellRing className="size-4" /> {t("notifications.enableHere")}</Button>
          )}
          {test.data && <span className="text-xs text-fg-muted">{t("notifications.sent", { n: test.data.sent })}</span>}
        </div>
      </Card>

      <Card className="space-y-2">
        <h3 className="font-semibold">{t("notifications.devices")}</h3>
        {list.isPending && <Spinner />}
        {list.data?.length === 0 && <p className="text-sm text-fg-muted">{t("notifications.none")}</p>}
        <ul className="divide-y divide-border">
          {(list.data ?? []).map((s) => (
            <DeviceRow key={s.id} sub={s} isThis={s.endpoint === endpoint} onToggle={(k, v) => update.mutate({ id: s.id, prefs: { [k]: v } })} onQuiet={(quiet) => update.mutate({ id: s.id, prefs: { quiet } })} onRemove={() => void askRemove(s.id, s.label || t("notifications.device"))} onTest={() => test.mutate(s.id)} />
          ))}
        </ul>
      </Card>
    </div>
  );
}

function DeviceRow({ sub, isThis, onToggle, onQuiet, onRemove, onTest }: { sub: PushSubscriptionInfo; isThis: boolean; onToggle: (k: (typeof KINDS)[number], v: boolean) => void; onQuiet: (q: PushPrefs["quiet"]) => void; onRemove: () => void; onTest: () => void }) {
  const { t, i18n } = useTranslation();
  // quiet hours in this browser's time zone: only failures and approval requests come through
  const q = sub.prefs.quiet ?? null;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2">
      <div className="min-w-40 flex-1">
        <div className="text-sm font-medium">{sub.label || t("notifications.device")} {isThis && <span className="ms-1 rounded-full bg-accent-soft px-1.5 text-[10px] text-accent">{t("notifications.thisDevice")}</span>}</div>
        <div className="text-[11px] text-fg-muted">{new Date(sub.createdAt).toLocaleDateString(i18n.language)}{sub.lastUsedAt ? ` · ${t("notifications.lastUsed")} ${new Date(sub.lastUsedAt).toLocaleString(i18n.language)}` : ""}</div>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        {KINDS.map((k) => (
          <label key={k} className="flex items-center gap-1.5">
            <input type="checkbox" className="accent-accent" checked={sub.prefs[k]} onChange={(e) => onToggle(k, e.target.checked)} /> {t(`notifications.kinds.${k}`)}
          </label>
        ))}
      </div>
      <div className="flex w-full flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1.5"><input type="checkbox" className="accent-accent" checked={!!q} onChange={(e) => onQuiet(e.target.checked ? { from: "22:00", to: "07:00", tz } : null)} /> {t("notifications.quiet")}</label>
        {q && (
          <>
            <input type="time" aria-label={t("notifications.quietFrom")} className="rounded border border-border bg-bg px-1.5 py-0.5" value={q.from} onChange={(e) => e.target.value && onQuiet({ ...q, from: e.target.value })} />
            <span>→</span>
            <input type="time" aria-label={t("notifications.quietTo")} className="rounded border border-border bg-bg px-1.5 py-0.5" value={q.to} onChange={(e) => e.target.value && onQuiet({ ...q, to: e.target.value })} />
            <span className="text-fg-muted">{t("notifications.quietHint")}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button type="button" className="rounded p-1 text-fg-muted hover:text-fg" title={t("notifications.sendTest")} onClick={onTest}><Send className="size-3.5" /></button>
        <button type="button" className="rounded p-1 text-fg-muted hover:text-danger" title={t("common.delete")} onClick={onRemove}><Trash2 className="size-3.5" /></button>
      </div>
    </li>
  );
}
